import { Box, useTheme } from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import StudentMeetingCard from './StudentMeetingCard';

const GAP = 10;
const PADDING = 16;
const CARD_ASPECT_RATIO = 16 / 10;

const calculateLayout = (width, height, count) => {
    const availableWidth = Math.max(0, width - PADDING * 2);
    const availableHeight = Math.max(0, height - PADDING * 2);
    let best = null;

    if (count === 2 && availableWidth >= availableHeight) {
        const cardWidth = (availableWidth - GAP) / 2;
        const fittedWidth = Math.min(cardWidth, availableHeight * CARD_ASPECT_RATIO);
        return {
            columns: 2,
            cardWidth: fittedWidth,
            cardHeight: fittedWidth / CARD_ASPECT_RATIO,
            area: fittedWidth * (fittedWidth / CARD_ASPECT_RATIO),
        };
    }

    for (let columns = 1; columns <= count; columns += 1) {
        const rows = Math.ceil(count / columns);
        const widthLimit = (availableWidth - GAP * (columns - 1)) / columns;
        const heightLimit = (availableHeight - GAP * (rows - 1)) / rows;
        const cardWidth = Math.min(widthLimit, heightLimit * CARD_ASPECT_RATIO);
        const cardHeight = cardWidth / CARD_ASPECT_RATIO;
        const area = cardWidth * cardHeight;

        if (cardWidth > 0 && cardHeight > 0 && (!best || area > best.area)) {
            best = { columns, cardWidth, cardHeight, area };
        }
    }

    return best;
};

const MeetingLayout = ({
    attendees,
    tutorStream,
    meetingStarted,
    handleTutorAudio,
    handleTutorCam,
    handleStudentAudio,
    handleStudentCam,
}) => {
    const theme = useTheme();
    const containerRef = useRef();
    const [layout, setLayout] = useState();
    const [portalTarget, setPortalTarget] = useState(null);

    const updateLayout = useCallback(() => {
        const container = containerRef.current;
        if (!container || !attendees.length) {
            setLayout(undefined);
            return;
        }

        const bounds = container.getBoundingClientRect();
        setLayout(calculateLayout(bounds.width, bounds.height, attendees.length));
    }, [attendees.length]);

    useEffect(() => {
        if (!meetingStarted || !portalTarget) return undefined;
        updateLayout();
        const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(updateLayout);
        if (containerRef.current) observer?.observe(containerRef.current);
        window.addEventListener('resize', updateLayout);
        return () => {
            observer?.disconnect();
            window.removeEventListener('resize', updateLayout);
        };
    }, [meetingStarted, portalTarget, updateLayout]);

    useEffect(() => {
        if (!meetingStarted) {
            setPortalTarget(null);
            return;
        }
        setPortalTarget(document.querySelector('[data-classroom-stage="true"]'));
    }, [meetingStarted]);

    if (!meetingStarted || !portalTarget) return null;

    return ReactDOM.createPortal(
        <Box
            ref={containerRef}
            data-testid="meeting-grid"
            data-columns={layout?.columns || 0}
            sx={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                zIndex: 20,
                display: 'grid',
                gridTemplateColumns: layout ? `repeat(${layout.columns}, ${layout.cardWidth}px)` : '1fr',
                gridAutoRows: layout ? `${layout.cardHeight}px` : 'minmax(0, 1fr)',
                gap: `${GAP}px`,
                p: `${PADDING}px`,
                boxSizing: 'border-box',
                alignContent: 'center',
                justifyContent: 'center',
                overflow: 'auto',
                backgroundColor: theme.palette.background.gray,
                '@media (max-width: 900px)': {
                    p: 1,
                },
            }}
        >
            {layout && attendees.map((attendee) => (
                <StudentMeetingCard
                    key={`${attendee.user_id}-${attendee.socket_id}`}
                    cardDimensions={layout}
                    attendee={attendee}
                    toturStream={tutorStream}
                    handleStreamTutorAudio={handleTutorAudio}
                    handleStreamTutorCam={handleTutorCam}
                    handleStreamStudentAudio={handleStudentAudio}
                    handleStreamStudentCam={handleStudentCam}
                />
            ))}
        </Box>,
        portalTarget,
    );
};

export default MeetingLayout;
